/**
 * InviteDialog — Invite a collaborator to the agency
 * Two states: form (input) → confirmation (temp password display)
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import { colors } from '../../styles/colors';

const ALL_PAGES = ['radar', 'comprendre', 'agir', 'mesurer'];
const PAGE_LABELS = { radar: 'Radar', comprendre: 'Comprendre', agir: 'Agir', mesurer: 'Mesurer' };

const ROLE_HELP = {
  manager: 'Le manager peut consulter toutes les pages accessibles, creer des actions et modifier les statuts.',
  viewer: 'Le viewer peut uniquement consulter les donnees des pages auxquelles il a acces.',
};

const textFieldSx = {
  '& .MuiInputLabel-root': { fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem' },
  '& .MuiInputBase-input': { fontFamily: '"DM Sans", sans-serif', fontSize: '0.9rem' },
  '& .MuiOutlinedInput-root': { borderRadius: '12px' },
};

export default function InviteDialog({ open, onClose, brands, brandPlans, onInvite }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Form state
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [brandAccess, setBrandAccess] = useState({}); // { brandId: [pages] }
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Confirmation state
  const [confirmationData, setConfirmationData] = useState(null);
  const [copied, setCopied] = useState(false);

  const resetForm = () => {
    setFullName('');
    setEmail('');
    setRole('viewer');
    setBrandAccess({});
    setError(null);
    setConfirmationData(null);
    setCopied(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Get allowed pages for a brand based on its BrandPlan
  const getAllowedPages = (brandId) => {
    const plan = brandPlans?.find(bp => bp.brand_id === brandId);
    if (!plan?.pages || plan.pages.length === 0) return ALL_PAGES; // default all
    return plan.pages;
  };

  const handleToggleBrand = (brandId) => {
    setBrandAccess(prev => {
      const next = { ...prev };
      if (next[brandId]) {
        delete next[brandId];
      } else {
        next[brandId] = [...getAllowedPages(brandId)];
      }
      return next;
    });
  };

  const handleTogglePage = (brandId, page) => {
    setBrandAccess(prev => {
      const pages = prev[brandId] || [];
      const next = pages.includes(page)
        ? pages.filter(p => p !== page)
        : [...pages, page];
      return { ...prev, [brandId]: next };
    });
  };

  const handleSelectAll = () => {
    const allAccess = {};
    brands.forEach(b => {
      allAccess[b.id] = [...getAllowedPages(b.id)];
    });
    setBrandAccess(allAccess);
  };

  const selectedBrandIds = Object.keys(brandAccess).map(Number);
  const hasEmptyPages = selectedBrandIds.some(id => (brandAccess[id] || []).length === 0);
  const canSubmit = email.trim() && fullName.trim() && selectedBrandIds.length > 0 && !hasEmptyPages;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    try {
      // Build access list per brand with per-brand pages
      const accessList = selectedBrandIds.map(brandId => ({
        brand_id: brandId,
        pages_access: brandAccess[brandId] || [],
      }));

      const result = await onInvite({
        email: email.trim().toLowerCase(),
        full_name: fullName.trim(),
        role,
        brand_ids: selectedBrandIds,
        pages_access: accessList[0]?.pages_access || ALL_PAGES, // API expects flat pages_access
      }, accessList);

      setConfirmationData(result);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erreur lors de l\'invitation';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!confirmationData?.temp_password) return;
    try {
      await navigator.clipboard.writeText(confirmationData.temp_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* fallback silent */ }
  };

  // --- Confirmation screen ---
  if (confirmationData) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '20px',
            backdropFilter: 'blur(16px)',
            background: colors.cardGlass,
            border: isMobile ? 'none' : `1px solid ${colors.borderLight}`,
          },
        }}
      >
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: colors.success }} />
            <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '1.1rem', fontWeight: 500, color: colors.charcoal, textAlign: 'center' }}>
              {confirmationData.temp_password ? 'Invitation envoyee' : 'Collaborateur reactive'}
            </Typography>
            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', color: colors.textMed, textAlign: 'center' }}>
              {confirmationData.temp_password
                ? `Un email avec les identifiants a ete envoye a ${email}. Vous pouvez aussi les copier ci-dessous :`
                : `${confirmationData.full_name || email} a ete reactive. Il conserve son ancien mot de passe.`
              }
            </Typography>

            {confirmationData.temp_password && (
              <Box sx={{ width: '100%', background: colors.ivory, borderRadius: '12px', border: `1px solid ${colors.borderLight}`, p: '18px 22px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.72rem', color: colors.textMed, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                    EMAIL
                  </Typography>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.88rem', color: colors.charcoal, fontWeight: 500 }}>
                    {email}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.72rem', color: colors.textMed, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                    MOT DE PASSE
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontFamily: '"SF Mono", "Fira Code", "Courier New", monospace', fontSize: '0.95rem', letterSpacing: '0.05em', color: colors.charcoal, fontWeight: 600 }}>
                      {confirmationData.temp_password}
                    </Typography>
                    <Tooltip title={copied ? 'Copie !' : 'Copier'}>
                      <IconButton size="small" onClick={handleCopy} aria-label="Copier le mot de passe" sx={{ color: colors.textMed, '&:hover': { color: colors.primary } }}>
                        <ContentCopyIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            )}

            {confirmationData.temp_password && (
              <Alert severity="warning" sx={{ mt: 1, borderRadius: '12px', fontFamily: '"DM Sans", sans-serif', fontSize: '0.8rem', width: '100%' }}>
                Ce mot de passe ne sera plus visible apres fermeture. Notez-le maintenant.
              </Alert>
            )}

            <Button
              variant="contained"
              fullWidth
              onClick={handleClose}
              sx={{
                mt: 1,
                borderRadius: '100px',
                textTransform: 'none',
                fontFamily: '"DM Sans", sans-serif',
                fontWeight: 600,
                bgcolor: colors.primary,
                '&:hover': { bgcolor: colors.primaryLight },
              }}
            >
              Fermer
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // --- Form screen ---
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '20px',
          backdropFilter: 'blur(16px)',
          background: colors.cardGlass,
          border: isMobile ? 'none' : `1px solid ${colors.borderLight}`,
        },
      }}
      BackdropProps={{ sx: { backdropFilter: 'blur(4px)' } }}
    >
      <DialogTitle sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '1.3rem', fontWeight: 600, color: colors.charcoal, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Inviter un collaborateur
        {isMobile && (
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: '8px !important' }}>
        <TextField
          fullWidth label="Nom complet" placeholder="Ex: Marie Dupont"
          value={fullName} onChange={e => setFullName(e.target.value)}
          required sx={textFieldSx}
        />

        <TextField
          fullWidth label="Adresse email" placeholder="marie@agence.com"
          type="email" value={email} onChange={e => setEmail(e.target.value)}
          required
          helperText="Un mot de passe temporaire sera genere automatiquement"
          sx={textFieldSx}
          FormHelperTextProps={{ sx: { fontFamily: '"DM Sans", sans-serif', fontSize: '0.72rem', color: colors.textLight } }}
        />

        <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
          <InputLabel sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem' }}>Role</InputLabel>
          <Select value={role} onChange={e => setRole(e.target.value)} label="Role" sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.9rem' }}>
            <MenuItem value="manager">Manager</MenuItem>
            <MenuItem value="viewer">Viewer</MenuItem>
          </Select>
          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.75rem', color: colors.textLight, mt: 0.5, lineHeight: 1.5 }}>
            {ROLE_HELP[role]}
          </Typography>
        </FormControl>

        {/* Brand access selection */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.85rem', color: colors.charcoal }}>
              Acces aux marques
            </Typography>
            {brands.length > 0 && (
              <Typography
                onClick={handleSelectAll}
                sx={{ fontSize: '0.72rem', fontFamily: '"DM Sans", sans-serif', fontWeight: 600, color: colors.primary, cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: colors.primaryLight } }}
              >
                Tout selectionner
              </Typography>
            )}
          </Box>

          {brands.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: '12px', fontSize: '0.82rem' }}>
              Aucune marque. Creez une marque d'abord.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {brands.map(brand => {
                const isSelected = !!brandAccess[brand.id];
                const selectedPages = brandAccess[brand.id] || [];
                const allowedPages = getAllowedPages(brand.id);

                return (
                  <Box
                    key={brand.id}
                    sx={{
                      background: colors.ivory,
                      borderRadius: '12px',
                      p: '14px 18px',
                      border: `1px solid ${isSelected ? colors.primary : colors.borderLight}`,
                      transition: 'border-color 0.2s ease',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleToggleBrand(brand.id)}
                          sx={{ color: colors.textLight, '&.Mui-checked': { color: colors.primary } }}
                        />
                      }
                      label={
                        <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', fontWeight: 600, color: colors.charcoal }}>
                          {brand.name}
                        </Typography>
                      }
                    />

                    {isSelected && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 4, mt: 0.5 }}>
                        {ALL_PAGES.map(page => {
                          const allowed = allowedPages.includes(page);
                          const active = selectedPages.includes(page);
                          return (
                            <Chip
                              key={page}
                              label={PAGE_LABELS[page]}
                              onClick={allowed ? () => handleTogglePage(brand.id, page) : undefined}
                              disabled={!allowed}
                              sx={{
                                height: 28,
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                fontFamily: '"DM Sans", sans-serif',
                                borderRadius: '100px',
                                cursor: allowed ? 'pointer' : 'default',
                                transition: 'all 0.2s ease',
                                bgcolor: active ? `${colors.primary}14` : 'transparent',
                                color: active ? colors.primary : (allowed ? colors.textLight : colors.borderLight),
                                border: `1px solid ${active ? colors.primary : colors.borderLight}`,
                                '&.Mui-disabled': {
                                  opacity: 0.5,
                                },
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}

                    {isSelected && selectedPages.length === 0 && (
                      <Typography sx={{ ml: 4, mt: 0.5, fontSize: '0.72rem', color: colors.danger, fontFamily: '"DM Sans", sans-serif' }}>
                        Selectionnez au moins une page
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ borderRadius: '12px', fontSize: '0.82rem' }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: '100px',
            textTransform: 'none',
            fontFamily: '"DM Sans", sans-serif',
            fontWeight: 600,
            color: colors.textMed,
            borderColor: colors.border,
            '&:hover': { borderColor: colors.charcoal, bgcolor: 'rgba(0,0,0,0.03)' },
          }}
        >
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
          sx={{
            borderRadius: '100px',
            textTransform: 'none',
            fontFamily: '"DM Sans", sans-serif',
            fontWeight: 600,
            bgcolor: colors.primary,
            color: colors.white,
            '&:hover': { bgcolor: colors.primaryLight },
            '&.Mui-disabled': { bgcolor: colors.borderLight, color: colors.textLight },
          }}
        >
          Inviter
        </Button>
      </DialogActions>
    </Dialog>
  );
}
